import {
  ref,
  onBeforeUnmount,
  computed,
  watch,
} from 'vue'

import useEventListener from '@nicolaudie-group/ng-ui-kit/composables/useEventListener'
import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'
import useThrottle from '@nicolaudie-group/ng-ui-kit/composables/useThrottle'

import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'

/**
 * @callback DropHandler
 * @param {*} draggedItemData - Data provided to the dragged item (see useDrag)
 * @param {*} dropZoneData - Data provided to the drop zone (see useDrop)
 */

/**
 * @typedef DragDropContext
 * @property {DropHandler} onDrop - Callback to call when a draggableItem is dropped
 * on a dropZone
 * @property {Boolean} isDragging - True when one of the draggableItems
 * is being dragged (computed)
 * @property {DropZone} activeDropZone - Currently active (as in "dragging something on it")
 * dropZone, or null if no dropZone is currently being dragged on (computed)
 */

const DROP_HANDLER = Symbol('onDrop')

const SET_DRAGGED_ITEM = Symbol('setDraggedItem')
const CLEAR_DRAGGED_ITEM = Symbol('clearDraggedItem')
const SET_ACTIVE_DROP_ZONE = Symbol('setActiveDropZone')
const CLEAR_ACTIVE_DROP_ZONE = Symbol('clearActiveDropZone')

const MOUSE_HANDLER = Symbol('mouseHandler')
const TOUCH_HANDLER = Symbol('touchHandler')

class DragDropContext {
  constructor(options = {}) {
    this.x = ref(0)
    this.y = ref(0)

    /**
     * Draggable item currently being dragged
      */
    this.draggedItem = ref(null)

    /**
      * Currently active (as in "dragging something on it") dropZone,
      * or null if no dropZone is currently being dragged on (computed)
    */
    this.activeDropZone = ref(null)

    this.onDrop = options.onDrop

    /**
     * True when one of the draggableItems
     * is being dragged (computed)
    */
    this.isDragging = computed(() => {
      dev.log('Computing "isDragging"')
      return !!this.draggedItem.value
    })

    const mouseHandler = useThrottle(this[MOUSE_HANDLER].bind(this))
    const touchHandler = useThrottle(this[TOUCH_HANDLER].bind(this))

    useEventListener(window, 'mousedown', mouseHandler)
    useEventListener(window, 'mousemove', mouseHandler, { passive: true })
    useEventListener(window, 'touchstart', touchHandler)
    useEventListener(window, 'touchmove', touchHandler, { passive: true })
  }

  [SET_DRAGGED_ITEM](draggableItem) {
    dev.log('Updating dragged item')
    this.draggedItem.value = draggableItem
  }

  [SET_ACTIVE_DROP_ZONE](dropZone) {
    if (!this.activeDropZone.value || this.activeDropZone.value.id !== dropZone.id) {
      dev.log('Updating dropzone')
      this.activeDropZone.value = dropZone
    }
  }

  [CLEAR_ACTIVE_DROP_ZONE]() {
    dev.log('Clearing dropzone')
    this.activeDropZone.value = null
  }

  [CLEAR_DRAGGED_ITEM]() {
    dev.log('Clearing dragged item')
    this.draggedItem.value = null
    // Also clear the drop zone, since if we're not dragging anything no dropzone is active
    this[CLEAR_ACTIVE_DROP_ZONE]()
  }

  /**
   * Callback to call when a draggableItem is dropped
   * on a dropZone
   */
  [DROP_HANDLER]() {
    if (this.activeDropZone.value && typeof this.onDrop === 'function') {
      // If there is a dropZone that's currently active (hovered)
      // & we have a drop callback
      this.onDrop(this.draggedItem.value.data, this.activeDropZone.value.data)
    }
    this[CLEAR_DRAGGED_ITEM]()
  }

  [MOUSE_HANDLER](event) {
    const { clientX, clientY } = event
    this.x.value = clientX
    this.y.value = clientY
  }

  [TOUCH_HANDLER](event) {
    const { targetTouches } = event
    if (targetTouches.length === 1) {
      this.x.value = targetTouches[0].pageX
      this.y.value = targetTouches[0].pageY
    }
  }
}

/**
 * Create/use a drag/drop context
 * This context can then be used by useDrag and useDrop composables
 * @param {Partial<DragDropContext>} context - Initial context
 * @returns {DragDropContext} A drag/drop context to provide to useDrag & useDrop
 */
export function useDragDropContext(context = {}) {
  if (context instanceof DragDropContext) {
    // If the context already was initialized, return it as is
    return context
  }

  return new DragDropContext(context)
}

/**
 * @typedef DraggableItem
 * @property {import('vue').Ref<Number>} dragX - Absolute horizontal position of the drag
 * @property {import('vue').Ref<Number>} dragY - Absolute vertical position of the drag
 * @property {import('vue').Ref<Boolean>} isDragging - True if the draggableItem is
 * currently being dragged
 * @property {*} data - Data to provide to the "onDrop" callback of the drag/drop context when
 * the draggableItem is dropped in said drag/drop context
 */

/**
 * Make an HTML element draggable in the provided context (creates a draggableItem)
 * @param {Object} target - Ref of the HTML element to make draggable
 * @param {DragDropContext} context - Drag/drop context in which the target is draggable
 * @param {*} data - Data to pass to the "onDrop" callback of the drag/drop context when the
 * element is dropped on a dropZone belonging to said context
 * @returns {DraggableItem}
 */
export function useDrag(target, context, data, enabled = true) {
  const isDragging = ref(false)
  const currentContext = useDragDropContext(context)
  const dataRef = useRef(data)
  const enabledRef = useRef(enabled)

  const id = Symbol('DRAGGABLE_ITEM_ID')

  const dragX = computed(() => {
    const x = currentContext.x.value
    if (isDragging.value) {
      return x
    }
    return 0
  })

  const dragY = computed(() => {
    const y = currentContext.y.value
    if (isDragging.value) {
      return y
    }
    return 0
  })

  const draggableItem = {
    id,
    dragX,
    dragY,
    isDragging,
    data: dataRef,
    enabled: enabledRef,
  }

  function startDragging() {
    if (enabledRef.value) {
      isDragging.value = true
      currentContext[SET_DRAGGED_ITEM](draggableItem)
    }
  }

  function stopDragging() {
    if (isDragging.value) {
      currentContext[DROP_HANDLER]()
      isDragging.value = false
    }
  }

  watch(enabledRef, (newEnabled) => {
    if (!newEnabled) {
      stopDragging()
    }
  })

  // clean it up
  onBeforeUnmount(() => {
    stopDragging()
  })

  function mouseDownHandler(event) {
    event.preventDefault()
    startDragging()
  }

  function touchStartHandler(event) {
    event.preventDefault()
    const { targetTouches } = event
    if (targetTouches.length === 1) {
      startDragging()
    }
  }

  function mouseUpHandler(event) {
    stopDragging(event)
  }

  function touchEndHandler(event) {
    stopDragging(event)
  }

  useEventListener(target, 'mousedown', mouseDownHandler)
  useEventListener(target, 'touchstart', touchStartHandler)
  useEventListener(window, 'mouseup', mouseUpHandler)
  useEventListener(window, 'touchend', touchEndHandler)

  return draggableItem
}

/**
 * @typedef DropZone
 * @property {Boolean} active - True if the dropZone is currently being hovered
 * with a draggable item from the same drag/drop context
 * @property {*} data - Data to provide to the "onDrop" callback of the drag/drop context when
 * a draggableItem from the same drag/drop context is dropped in this dropZone
 */

/**
 * @callback CanDrop
 * @param {*} draggedItemData - Data provided to the dragged item (see useDrag)
 * @param {*} dropZoneData - Data provided to the drop zone (see useDrop)
 */

/**
 * Make an HTML element accept draggable elements in the provided context (creates a dropZone)
 * @param {Object} target - Ref of the HTML element that will receive draggable elements
 * @param {DragDropContext} context - Drag/drop context from which the dropZone accepts elements
 * @param {*} data - Data to pass to the "onDrop" callback of the drag/drop context when an element
 * is dropped on the dropZone
 * @param {CanDrop} canDrop - Function that should return true or false depending if the drop
 * is allowed
 * @returns {DropZone}
 */
export function useDrop(target, context, data, canDrop) {
  const currentContext = useDragDropContext(context)
  const currentData = useRef(data)

  const active = ref(false)

  const id = Symbol('DROPZONE_ID')

  const dropZone = {
    id,
    active,
    data: currentData,
  }

  function enterZone() {
    if (typeof canDrop !== 'function' || canDrop(currentContext.draggedItem.value.data, currentData)) {
      currentContext[SET_ACTIVE_DROP_ZONE](dropZone)
      active.value = true
    }
  }

  function leaveZone() {
    if (active.value) {
      currentContext[CLEAR_ACTIVE_DROP_ZONE]()
      active.value = false
    }
  }

  function isOnTarget(x, y) {
    const targetBounds = target.value.getBoundingClientRect()
    const result = (
      x >= targetBounds.left
      && x < targetBounds.right
      && y >= targetBounds.top
      && y < targetBounds.bottom
    )
    return result
  }

  watch([currentContext.isDragging, currentContext.x, currentContext.y], ([isDragging, x, y]) => {
    if (isDragging && isOnTarget(x, y)) {
      // If the dragging has started
      // & the cursor/finger is over the target, but the target is not yet active
      // We probably just entered the target
      enterZone()
    } else {
      // If the dragging has been stopped
      // & the cursor/finger is not over the target, but the target is active
      // We probably just left the target
      leaveZone()
    }
  }, { flush: 'sync' })

  // clean it up
  onBeforeUnmount(() => {
    leaveZone()
  })

  // REASON FOR NOT USING MOUSEENTER/MOUSELEAVE:
  // There is a bug in chrome.
  // When the cursor is between 2 dropzones that are next to each other
  // it keeps switching between them very fast. So instead we use mouseMove for now.
  // function mouseEnterHandler() {
  //   enterZone()
  // }
  // function mouseLeaveHandler() {
  //   leaveZone()
  // }
  // useEventListener(target, 'mouseenter', mouseEnterHandler)
  // useEventListener(target, 'mouseleave', mouseLeaveHandler)

  return dropZone
}
